import * as dayjs from 'dayjs';

import { Subject, Observable, of } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl } from '@angular/forms';
import { Component, OnInit, EventEmitter, Input, Output, OnDestroy } from '@angular/core';

import { DateService } from '@service/date.service';
import { CommonService } from '@service/common.service';
import { QuotationService } from '@service/quotation.service';
import { MasterDataService } from '@service/master-data.service';
import { AuthenticationService } from '@service/authentication.service';

import { QuoteData } from '@model/quotation.model';
import {
  Country,
  ImdCodeName,
  Discount,
  Province,
  InsuranceType,
  InsurancePlan,
  AoMember,
  PackageType,
  Period,
  NumberOfPerson,
  MasterData
} from '@model/master-data.model';

import { UserType } from '@enum/authentication.enum';
import { PackageTypeCatagory, TravelType, ChannelType, InsuranceTypeCatagory, getPriceErrorType, StatusMode } from '@enum/quotation.enum';

import { transformQuoteFormToQuoteData } from '@mapping/quotation.mapping';
import {
  TYPE_LIST,
  NON_AO_MEMBER,
  DEFAULT_COUNTRY_LIST,
  DEFAULT_MAX_TO_DATE,
  GET_PRICE_DATE_FORMAT,
  STUDENT_OVERSEA_TOP_LEVEL_ID,
  THAILAND_CODE,
  ANNUAL_TRIP_PERIOD_LIST
} from './quote-form.constant';

@Component({
  selector: 'app-quote-form',
  templateUrl: './quote-form.component.html',
  styleUrls: ['./quote-form.component.scss']
})
export class QuoteFormComponent implements OnInit, OnDestroy {
  @Input() errorMessageType: string;
  @Output() handleOnSubmit: any = new EventEmitter<any>();

  quoteForm: UntypedFormGroup;

  currentQuoteData: QuoteData;
  getPriceErrorType: typeof getPriceErrorType = getPriceErrorType;

  maxToDate: NgbDateStruct;
  minFromDate: NgbDateStruct;
  minToDate: NgbDateStruct;

  typeList: PackageType[] = TYPE_LIST;
  channel: typeof ChannelType = ChannelType;

  imdCodeList: ImdCodeName[];

  aoMemberList: AoMember[];
  insuranceTypeList: InsuranceType[];
  insurancePlanList: InsurancePlan[];
  typeofTravel: typeof TravelType = TravelType;
  directDiscount$: Observable<Discount[]>;

  departure$: Observable<Country[]>;
  destination$: Observable<Country[]>;
  defaultCountry: Country[] = DEFAULT_COUNTRY_LIST;

  periodList: Period[] = [];
  numPeopleList: NumberOfPerson[] = [];

  durationPeriodList: MasterData[] = ANNUAL_TRIP_PERIOD_LIST;

  maxSelectedCountry: number = 5;

  private onDestroy$: Subject<void> = new Subject();

  constructor(
    private dateService: DateService,
    private formBuilder: UntypedFormBuilder,
    private commonService: CommonService,
    private quotationService: QuotationService,
    private masterDataService: MasterDataService,
    private authenticationService: AuthenticationService
  ) {}

  ngOnInit(): void {
    this.quoteForm = this.getDefaultForm();

    this.setQuoteType();
    this.setNumberOfPeople();
    this.setDefaultTravellingDate();

    this.setChannelType();
    this.setInsuranceType();

    this.setCurentQuoteData();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onChangeType(): void {
    if (this.type.value.code === PackageTypeCatagory.INDIVIDUAL) {
      this.numPeopleList = this.quotationService.getQuoteNumberOfPerson(1, 6);
      this.travelType.enable();
    } else if (this.type.value.code === PackageTypeCatagory.FAMILY) {
      this.numPeopleList = this.quotationService.getQuoteNumberOfPerson(2, 4);
      this.travelType.disable();
    } else {
      this.numPeopleList = this.quotationService.getQuoteNumberOfPerson(1, 60);
      this.travelType.disable();
    }
    this.numPeople.setValue(this.numPeopleList[0]);
    this.travelType.setValue(TravelType.SINGLE_TRIP);

    this.setInsuranceType();
  }

  onChangeChannelType(): void {
    this.directDiscount.setValue(null);
    this.discountRate.setValue(null);
    this.imdCode.setValue(null);
    this.aoMember.setValue(null);

    if (!this.isDirectChannel()) {
      this.imdCode.setValidators(Validators.required);
      this.directDiscount.setValidators(null);
      this.discountRate.setValidators(null);
      this.setImdCode();
    } else {
      this.imdCode.setValidators(null);
      this.directDiscount.setValidators(Validators.required);
      this.discountRate.setValidators(Validators.required);
      this.discountRate.setValue('0');
    }

    this.imdCode.updateValueAndValidity();
    this.directDiscount.updateValueAndValidity();
    this.discountRate.updateValueAndValidity();
  }

  onChangeImdCode(): void {
    if (this.imdCode.value) {
      this.setAoMember(this.imdCode.value.intermediaryId);
    } else {
      this.aoMember.setValue(null);
    }
  }

  onChangeInsuranceType(): void {
    this.setInsurancePlan(this.insuranceType.value.categoryId);
    if (this.type.value.code === PackageTypeCatagory.INDIVIDUAL) {
      if (this.isDomesticInsuranceType()) {
        this.travelType.setValue(TravelType.SINGLE_TRIP);
        this.travelType.disable();
      } else {
        this.travelType.enable();
      }
    }
  }

  onChangeInsurancePlan(): void {
    this.setPeriod();
    this.toggleSchoolName();
    this.setDropdownbySubProductId();
  }

  onChangePeriod(): void {
    this.setDateByPeriod();
  }

  onChangeTravelType(): void {
    this.setFromToData();
    this.setDateData();
    this.durationPeriod.setValue(this.durationPeriodList[0]);
  }

  onChangeFromDate(): void {
    const fromDateInDateFormat: Date = this.dateService.convertToDateFormat(this.fromDate.value);
    this.minToDate = this.dateService.convertToDatepickerFormat(fromDateInDateFormat, 0);

    if (this.isStudentOverseaPlan() && this.period.value) {
      this.setDateByPeriod();
    } else {
      this.setDateData();
    }
  }

  onChangeToDate(): void {
    this.setDuration();
  }

  onDirectDiscountchange(): void {
    if (this.directDiscount.value) {
      this.discountRate.setValue(this.directDiscount.value.discountRateMax);
    } else {
      this.discountRate.setValue('0');
    }
  }

  isGetPriceError(): boolean {
    return this.errorMessageType === this.getPriceErrorType.UNEXPECTED;
  }

  isInternalUser(): boolean {
    return this.authenticationService.getUserType() === UserType.INTERNAL;
  }

  isDirectChannel(): boolean {
    return this.channelType.value === ChannelType.DIRECT;
  }

  isDomesticInsuranceType(): boolean {
    return this.insuranceType.value && this.insuranceType.value.type === InsuranceTypeCatagory.DOMESTIC;
  }

  isStudentOverseaPlan(): boolean {
    return this.insurancePlan.value && this.insurancePlan.value.topLevelId === STUDENT_OVERSEA_TOP_LEVEL_ID;
  }

  isAnnualTrip(): boolean {
    return this.travelType.value === TravelType.ANNUAL_TRIP;
  }

  search = (term: string, item: MasterData): boolean => this.commonService.searchMasterDataDropdown(term, item);

  onSubmit(): void {
    if (this.travelType.value === TravelType.ANNUAL_TRIP) {
      this.period.setValue(this.periodList[0]); // hard code 90 for dance move
    }

    this.commonService.markFormGroupTouched(this.quoteForm);
    if (this.quoteForm.valid) {
      const quoteData: QuoteData = transformQuoteFormToQuoteData(this.quoteForm.getRawValue());

      if (!isEqual(quoteData, this.currentQuoteData)) {
        this.quotationService.changeQuoteData({
          ...quoteData,
          dateWhenGetPrice: dayjs().format(GET_PRICE_DATE_FORMAT)
        });
      }
      this.handleOnSubmit.emit(StatusMode.VIEW);
    }
  }

  private setCurentQuoteData(): void {
    this.quotationService
      .getQuoteData()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data: QuoteData) => {
        this.currentQuoteData = data;
      });
  }

  private setPeriod(): void {
    this.periodList = this.quotationService.getQuotePeriod();
  }

  private setQuoteType(): void {
    this.type.setValue(this.typeList[0]);
  }

  private setNumberOfPeople(): void {
    this.numPeopleList = this.quotationService.getQuoteNumberOfPerson(1, 6);
    this.numPeople.setValue(this.numPeople.value || this.numPeopleList[0]);
  }

  private setDefaultTravellingDate(): void {
    const today: NgbDateStruct = this.dateService.convertToDatepickerFormat(new Date());
    this.fromDate.setValue(today);
    this.toDate.setValue(today, 1);

    const backDate: Date = new Date();
    backDate.setDate(backDate.getDate() - 6);

    this.minFromDate = this.isInternalUser() ? this.dateService.convertToDatepickerFormat(backDate) : today;

    const minFromDateInDateFormat: Date = this.dateService.convertToDateFormat(this.fromDate.value);
    this.maxToDate = this.dateService.convertToDatepickerFormat(minFromDateInDateFormat, DEFAULT_MAX_TO_DATE);
  }

  private setDateData(): void {
    const fromDateInDateFormat: Date = this.dateService.convertToDateFormat(this.fromDate.value);
    this.minToDate = this.dateService.convertToDatepickerFormat(fromDateInDateFormat, 0);
    this.maxToDate = this.dateService.convertToDatepickerFormat(fromDateInDateFormat, DEFAULT_MAX_TO_DATE);

    if (this.travelType.value === TravelType.SINGLE_TRIP) {
      this.toDate.setValue(this.dateService.convertToDatepickerFormat(fromDateInDateFormat, 1));
      this.toDate.enable();
    } else {
      const toDatebyAnnual: NgbDateStruct = this.dateService.calculateDateNextYear(this.fromDate.value);
      this.toDate.setValue(toDatebyAnnual);
      this.toDate.disable();
    }

    this.setDuration();
  }

  private setDateByPeriod(): void {
    const maxToDate: number = this.period.value.code - 1;
    const fromDateInDateFormat: Date = this.dateService.convertToDateFormat(this.fromDate.value);
    this.maxToDate = this.dateService.convertToDatepickerFormat(fromDateInDateFormat, maxToDate);
    this.toDate.setValue(this.maxToDate);
    this.toDate.enable();

    this.setDuration();
  }

  private setDuration(): void {
    const duration: number = this.dateService.calculateDurationDatepickerFormat(this.fromDate.value, this.toDate.value);
    this.duration.setValue(duration);
  }

  private setChannelType(): void {
    if (this.isInternalUser()) {
      this.channelType.setValue(ChannelType.DIRECT);
    } else {
      this.imdCode.disable();
      this.aoMember.disable();
      this.channelType.disable();
      this.channelType.setValue(ChannelType.INTERMEDIARY);

      this.setImdCode();
    }

    this.onChangeChannelType();
  }

  private setImdCode(): void {
    this.masterDataService
      .getImdCode(this.authenticationService.getUserBranchCode())
      .pipe(take(1))
      .subscribe((imdCodeList: ImdCodeName[]) => {
        this.imdCodeList = imdCodeList;

        if (!this.isInternalUser()) {
          const userImdCode: string = this.authenticationService.getUserIMDCode();
          const currentImdCode: ImdCodeName = this.imdCodeList.filter((imdCode: ImdCodeName) => imdCode.intermediaryId === userImdCode)[0];

          this.imdCode.setValue(currentImdCode);

          if (currentImdCode) {
            this.setAoMember(currentImdCode.intermediaryId);
          }
        }
      });
  }

  private setAoMember(imdCode: string): void {
    this.masterDataService
      .getAoMember(imdCode)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data: AoMember[]) => {
        if (data.length !== 0) {
          this.aoMemberList = data;
        } else {
          this.aoMemberList = [
            {
              ...NON_AO_MEMBER,
              intermediaryId: imdCode
            }
          ];
        }
        this.aoMember.setValue(this.aoMemberList[0]);
      });
  }

  private setInsuranceType(): void {
    const currentPackageType: string = this.type.value ? this.type.value.code : PackageTypeCatagory.INDIVIDUAL;
    this.masterDataService
      .getInsuranceType(currentPackageType)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data: InsuranceType[]) => {
        this.insuranceTypeList = data;
        this.insuranceType.setValue(this.insuranceTypeList[0]);
        this.setInsurancePlan(this.insuranceType.value.categoryId);
      });
  }

  private setInsurancePlan(categoryId: string): void {
    this.masterDataService
      .getInsurancePlan(categoryId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data: InsurancePlan[]) => {
        this.insurancePlanList = data;
        this.insurancePlan.setValue(this.insurancePlanList[0]);
        this.onChangeInsurancePlan();
      });
  }

  private setDropdownbySubProductId(): void {
    this.setDepartureAndDestination();
    this.setDirectDiscount();
    this.setFromToData();
    if (this.isStudentOverseaPlan()) {
      this.period.setValue(this.periodList[0]);
      this.setDateByPeriod();
    } else {
      this.period.setValue(null);
      this.setDateData();
    }
  }

  private setDepartureAndDestination(): void {
    if (this.insuranceType.value.type === InsuranceTypeCatagory.DOMESTIC) {
      this.departure$ = this.destination$ = this.setProvince();
    } else {
      this.departure$ = this.destination$ = this.setCountry();
    }
  }

  private setDirectDiscount(): void {
    this.directDiscount$ = this.masterDataService.getDiscount(this.insurancePlan.value.subProductId).pipe(take(1));
  }

  private setFromToData(): void {
    this.from.setValue(null);
    this.to.setValue(null);
    this.from.enable();
    this.to.enable();

    this.setDepartureAndDestination();
    this.maxSelectedCountry = 5;

    switch (this.insuranceType.value.type) {
      case InsuranceTypeCatagory.INBOUND:
        if (this.travelType.value === TravelType.ANNUAL_TRIP) {
          this.from.disable();
          this.from.setValue(this.defaultCountry[2]);
        }
        this.to.disable();
        this.to.setValue([this.defaultCountry[0]]);
        break;
      case InsuranceTypeCatagory.OUTBOUND:
        if (this.travelType.value === TravelType.ANNUAL_TRIP) {
          this.destination$ = of(this.defaultCountry.filter((val: Country) => val.code !== THAILAND_CODE));
          this.maxSelectedCountry = 1;
        }
        this.from.disable();
        this.from.setValue(this.defaultCountry[0]);
        break;
      case InsuranceTypeCatagory.DOMESTIC:
        break;
      default:
        break;
    }
  }

  private setCountry(): Observable<Country[]> {
    return this.masterDataService.getCountryBySubProductId(this.insurancePlan.value.subProductId).pipe(take(1));
  }

  private setProvince(): Observable<Province[]> {
    return this.masterDataService.getProvinceBySubProductId(this.insurancePlan.value.subProductId).pipe(take(1));
  }

  private toggleSchoolName(): void {
    if (this.isStudentOverseaPlan()) {
      this.schoolName.setValidators(Validators.required);
    } else {
      this.schoolName.clearValidators();
    }
    this.schoolName.updateValueAndValidity();
  }

  private getDefaultForm(): UntypedFormGroup {
    return this.formBuilder.group({
      type: [null, Validators.required],
      channelType: [this.channel.DIRECT, Validators.required],
      imdCode: null,
      aoMember: null,
      insuranceType: [null, Validators.required],
      insurancePlan: [null, Validators.required],
      period: null,
      schoolName: null,
      travelType: [TravelType.SINGLE_TRIP, Validators.required],
      durationPeriod: null,
      from: [{ value: null, disabled: true }, Validators.required],
      to: [null, Validators.required],
      fromDate: [null, Validators.required],
      toDate: [null, Validators.required],
      numPeople: [null, Validators.required],
      duration: [{ value: '', disabled: true }, Validators.required],
      directDiscount: [null, Validators.required],
      discountRate: [{ value: '0', disabled: true }, Validators.required],
      quotationNumber: ['']
    });
  }

  get type(): AbstractControl {
    return this.quoteForm.get('type');
  }

  get channelType(): AbstractControl {
    return this.quoteForm.get('channelType');
  }

  get imdCode(): AbstractControl {
    return this.quoteForm.get('imdCode');
  }

  get aoMember(): AbstractControl {
    return this.quoteForm.get('aoMember');
  }

  get insuranceType(): AbstractControl {
    return this.quoteForm.get('insuranceType');
  }

  get insurancePlan(): AbstractControl {
    return this.quoteForm.get('insurancePlan');
  }

  get period(): AbstractControl {
    return this.quoteForm.get('period');
  }

  get schoolName(): AbstractControl {
    return this.quoteForm.get('schoolName');
  }

  get travelType(): AbstractControl {
    return this.quoteForm.get('travelType');
  }

  get durationPeriod(): AbstractControl {
    return this.quoteForm.get('durationPeriod');
  }

  get from(): AbstractControl {
    return this.quoteForm.get('from');
  }

  get to(): AbstractControl {
    return this.quoteForm.get('to');
  }

  get fromDate(): AbstractControl {
    return this.quoteForm.get('fromDate');
  }

  get toDate(): AbstractControl {
    return this.quoteForm.get('toDate');
  }

  get numPeople(): AbstractControl {
    return this.quoteForm.get('numPeople');
  }

  get duration(): AbstractControl {
    return this.quoteForm.get('duration');
  }

  get directDiscount(): AbstractControl {
    return this.quoteForm.get('directDiscount');
  }

  get discountRate(): AbstractControl {
    return this.quoteForm.get('discountRate');
  }
}
